import ErrorMessage from '@/components/common/elements/ErrorMessage';
import Text from '@/components/common/elements/Text';
import NoData from '@/components/common/elements/NoData';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import RecruitmentGrid from '@/components/domain/recruitment/RecruitmentGrid';
import PremiumRecruitmentSection, { RecruitmentCarouselPageIndicator as PageIndicator } from '@/components/domain/recruitment/PremiumRecruitmentSection';
import { useNavigate } from 'react-router-dom';
import CardListCarousel from '@/components/common/organism/CardListCarousel';
import TableList from '@/components/common/organism/TableList';
import RecruitmentTable from "@/pages/Home/components/RecruitmentTable";
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/api/repository/axios';
import { useListRestore } from '@/common/listRestore';
import { HomeRecruitmentList, BannerListItem, RecruitmentListItem } from '@/api/repository/types/types';
import { RecruitmentListData } from '@/components/domain/recruitment/types';
import { RecruitmentTableRow } from '@/pages/Home/components/RecruitmentTable';
import RecruitmentRowList from '@/pages/Home/components/RecruitmentRowList';
import styled from '@emotion/styled';
import SectionHeader from '@/pages/HomeV2/sections/SectionHeader';
import { claudeTokens } from '@/styles/claudeTokens';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// 추후 어드민 줄 수 설정을 연결할 자리 — 지금은 한 줄 두 장으로 고정.
const PREMIUM_MOBILE_ROWS: 1 | 2 = 1;

const RecruimentTableHeaders = ["치과명", "제목", "업무", "지역", "채용형태"]

const SectionWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isBanner' && prop !== 'compactBottom' && prop !== 'integrated'
})<{ isBanner?: boolean; compactBottom?: boolean; integrated?: boolean }>`
  margin-bottom: ${props => props.compactBottom ? '0.75rem' : props.isBanner ? '0.5rem' : '2rem'};

  @media (max-width: 768px) {
    margin-bottom: ${props => props.isBanner ? '0.25rem' : '0.5rem'};
  }

  ${props => props.integrated && `
    margin: 0;
    min-width: 0;
    padding: ${props.isBanner ? '0' : 'var(--home-section-padding-y, 18px) var(--home-section-padding, 16px)'};
    background: ${props.isBanner ? 'transparent' : claudeTokens.color.surface};
    border-radius: ${claudeTokens.radius.md};
    @media (max-width: 768px) {
      margin: 0;
      padding: ${props.isBanner ? '0' : 'var(--home-section-padding-y, 10px) var(--home-section-padding, 8px)'};
    }
  `}
`;

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: var(--home-section-gap, 24px);
  @media (max-width: 768px) { gap: var(--home-section-gap, 12px); }
`;

const SectionTitleWrapper = styled.div<{ spacedBottom?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 2rem 0 1rem 0;

  /* 추천 섹션은 데스크톱 목록(MUI Grid) 특성상 타이틀-카드 간격이 프리미엄보다 좁아 패딩으로 보정(데스크톱 전용).
     모바일은 두 목록 모두 padding-top:0 이라 보정하면 오히려 추천만 벌어지므로 미적용. */
  ${props => props.spacedBottom ? `
    @media (min-width: 769px) {
      padding-bottom: 10px;
    }
  ` : ''}

  @media (max-width: 768px) {
    margin: 0.75rem 0 0 0;
  }
`;

const SectionTitle = styled(Text)`
  @media (max-width: 768px) {
    font-size: 1.25rem !important;
    margin: 0 !important;
  }
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const MoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: 2px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;

  &:hover {
    color: #374151;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const BannerCarouselContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'integrated',
})<{ integrated?: boolean }>`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  margin-top: ${props => props.integrated ? '0' : '1rem'};

  .swiper-pagination {
    display: none;
  }

  @media (max-width: 768px) {
    margin-top: ${props => props.integrated ? '0' : '0.5rem'};
    border-radius: 0;
  }
`;

const BannerContainer = styled(Box)`
  width: 100%;
  cursor: pointer;
  overflow: hidden;
  aspect-ratio: 4 / 1;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  &:hover {
    opacity: 0.95;
    transition: opacity 0.3s ease;
  }
`;

/**
 * [sections] 이 섹션만 그린다(PREMIUM·RECOMMEND·BANNER 등 서버 type). [exclude] 이 섹션은 빼고 그린다.
 * 모바일 홈에서 프리미엄 채용만 핫글 위로 올릴 때 쓴다(2026-10-08) — 같은 'home:list' 데이터를 쓰므로 두 번 그려도 저장된 목록을 함께 쓴다.
 */
interface HomeListProps {
    /** 통합 홈 응답을 주입하면 개별 채용 API를 호출하지 않는다. */
    items?: HomeRecruitmentList[];
    integrated?: boolean;
    sections?: string[];
    exclude?: string[];
}

const HomeListContext = createContext<HomeListState | null>(null);
interface HomeListState {
    data: HomeRecruitmentList[] | null;
    loading: boolean;
    error: unknown;
}

const useHomeListState = (): HomeListState => {
    // 뒤로가기로 홈에 돌아오면 다시 받지 않고 그대로 되살린다 — 로딩 스피너로 화면 높이가 접히지 않아 스크롤 복원이 정확해진다
    const { restored, save } = useListRestore<HomeRecruitmentList[]>('home:list');
    const [data, setData] = useState<HomeRecruitmentList[] | null>(restored);
    const [loading, setLoading] = useState(restored === null);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        if (restored) return;
        let active = true;
        const fetchHomeList = async () => {
            try {
                setLoading(true);
                const response = await api.search.homeComponent();

                if (!active) return;
                setData(response.result);
                save(response.result ?? []);
            } catch (err) {

                if (active) setError(err);
            } finally {
                if (active) setLoading(false);
            }
        };
        fetchHomeList();
        return () => { active = false; };
    }, []);

    return { data, loading, error };
};

export const HomeListProvider = ({ children }: { children: ReactNode }) => {
    const state = useHomeListState();
    return <HomeListContext.Provider value={state}>{children}</HomeListContext.Provider>;
};

const StandaloneHomeList = (props: HomeListProps) => {
    const state = useHomeListState();
    return <HomeListContent {...props} state={state} />;
};

const HomeList = (props: HomeListProps) => {
    const state = useContext(HomeListContext);
    if (props.items !== undefined) return <HomeListContent {...props} state={{ data: props.items, loading: false, error: null }} />;
    return state ? <HomeListContent {...props} state={state} /> : <StandaloneHomeList {...props} />;
};

const HomeListContent = ({ integrated = false, sections, exclude, state: { data, loading, error } }: HomeListProps & { state: HomeListState }) => {
    const [premiumPage, setPremiumPage] = useState(1);
    const [recommendPage, setRecommendPage] = useState(1);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (loading) return <LoadingSpinner placement="inline" minHeight="160px" />;
    if (error) return <ErrorMessage />;
    // 홈에서 채용 데이터가 없을 땐 빈 섹션(데이터 없음 문구)을 아예 렌더하지 않음 (하단 네비 아래 고아 텍스트 방지)
    if (!data || data.length === 0) return null;

    const handleRowClick = (id: number) => {
        navigate(`/recruitments/${id}`);
    };

    const handleBannerClick = (url: string) => {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            window.location.href = url;
        } else {
            navigate(url);
        }
    };

    // 섹션 더보기: 같은 도메인이면 SPA 이동, 외부 URL이면 그대로 이동
    const handleMoreClick = (url: string) => {
        if (!url) return;
        if (url.startsWith('http://') || url.startsWith('https://')) {
            try {
                const parsed = new URL(url);
                if (parsed.origin === window.location.origin) {
                    navigate(parsed.pathname + parsed.search);
                    return;
                }
            } catch {
                // URL 파싱 실패 시 아래 외부 이동으로 처리
            }
            window.location.href = url;
            return;
        }
        navigate(url);
    };

    const isBannerItem = (item: any): item is BannerListItem => {
        return item.itemType === 'banner';
    };

    const isRecruitmentItem = (item: any): item is RecruitmentListItem => {
        return item.itemType === 'recruitment';
    };

    // PREMIUM 데이터 찾기 및 페이지 계산
    const premiumData = data.find(item => item.type === 'PREMIUM');
    const premiumMobilePages: any[][] = [];
    if (premiumData && premiumData.list) {
        for (let i = 0; i < premiumData.list.length; i += PREMIUM_MOBILE_ROWS * 2) {
            premiumMobilePages.push(premiumData.list.slice(i, i + PREMIUM_MOBILE_ROWS * 2));
        }
    }

    // RECOMMEND 데이터 찾기 및 페이지 계산
    const recommendData = data.find(item => item.type === 'RECOMMEND');
    const recommendMobilePages: any[][] = [];
    if (recommendData && recommendData.list) {
        for (let i = 0; i < recommendData.list.length; i += 4) {
            recommendMobilePages.push(recommendData.list.slice(i, i + 4));
        }
    }

    const renderSection = ({ type, title, list, link }: HomeRecruitmentList) => {
        if (integrated && list.length === 0) return null;
        const moreLink = link || (type === 'PREMIUM' ? '/recruitments/region?category=REGION&districts=0_0' : undefined);

        if (integrated && type === 'PREMIUM') {
            return (
                <SectionWrapper key={`${type}-${title}`} data-home-section="premium" integrated compactBottom>
                    <PremiumRecruitmentSection
                        homeControls
                        title={title}
                        data={list as unknown as RecruitmentListData[]}
                        onCardClick={handleRowClick}
                        onMoreClick={moreLink ? () => handleMoreClick(moreLink) : undefined}
                    />
                </SectionWrapper>
            );
        }

        return (
            <SectionWrapper key={`${type}-${title}`} data-home-section={type.toLowerCase()} integrated={integrated} isBanner={type === 'BANNER'} compactBottom={type === 'PREMIUM'}>
                {type !== 'BANNER' && (integrated ? (
                    <SectionHeader
                        title={title}
                        onMoreClick={moreLink ? () => handleMoreClick(moreLink) : undefined}
                        meta={type === 'PREMIUM' && premiumMobilePages.length > 1 ? (
                            <PageIndicator>{premiumPage} / {premiumMobilePages.length}</PageIndicator>
                        ) : type === 'RECOMMEND' && recommendMobilePages.length > 1 ? (
                            <PageIndicator>{recommendPage} / {recommendMobilePages.length}</PageIndicator>
                        ) : undefined}
                    />
                ) : (
                    <SectionTitleWrapper spacedBottom={type === 'RECOMMEND'}>
                        <TitleLeft>
                            <SectionTitle size='1.5rem' fontweight='bold'>
                                {title}
                            </SectionTitle>
                            {type === 'PREMIUM' && premiumMobilePages.length > 1 && (
                                <PageIndicator>
                                    {premiumPage} / {premiumMobilePages.length}
                                </PageIndicator>
                            )}
                            {type === 'RECOMMEND' && recommendMobilePages.length > 0 && (
                                <PageIndicator>
                                    {recommendPage} / {recommendMobilePages.length}
                                </PageIndicator>
                            )}
                        </TitleLeft>
                        {moreLink && (
                            <MoreLink onClick={() => handleMoreClick(moreLink)}>
                                {type === 'PREMIUM' ? '전체보기' : '더보기'}
                            </MoreLink>
                        )}
                    </SectionTitleWrapper>
                ))}
                {type === 'PREMIUM' && (
                    <Box>
                        <CardListCarousel
                            type='contents'
                            onClick={(id:number) => handleRowClick(id)}
                            arrowButton
                            data={list as unknown as RecruitmentListData[]}
                            rowLength={4}
                            mobileRows={PREMIUM_MOBILE_ROWS}
                            mobileFullWidthSingle
                            onPageChange={setPremiumPage}
                        />
                    </Box>
                )}
                {type === 'RECOMMEND' && <Box><RecruitmentGrid data={list as unknown as RecruitmentListData[]} onPageChange={setRecommendPage} /></Box>}
                {type === 'LOCAL' &&
                    <RecruitmentRowList list={list as unknown as RecruitmentTableRow[]} onClick={handleRowClick} />}
                {type === 'BANNER' && list.filter(isBannerItem).length > 0 && (
                    <BannerCarouselContainer integrated={integrated}>
                        <Swiper
                            modules={[Pagination, Autoplay]}
                            spaceBetween={0}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            autoplay={{
                                delay: 3000,
                                disableOnInteraction: false,
                            }}
                            loop={list.filter(isBannerItem).length > 1}
                            style={{ width: '100%', height: '100%' }}
                        >
                            {list.map((item, index) => {
                                if (isBannerItem(item)) {
                                    return (
                                        <SwiperSlide key={index}>
                                            <BannerContainer
                                                onClick={() => handleBannerClick(item.endpointUrl)}
                                            >
                                                <img
                                                    src={isMobile ? item.mobileBannerUrl : item.bannerUrl}
                                                    alt={title}
                                                />
                                            </BannerContainer>
                                        </SwiperSlide>
                                    );
                                }
                                return null;
                            })}
                        </Swiper>
                    </BannerCarouselContainer>
                )}
            </SectionWrapper>
        );
    };

    const shown: HomeRecruitmentList[] = data.filter((section: HomeRecruitmentList) =>
        (!integrated || section.list.length > 0) &&
        (!sections || sections.includes(section.type)) && !(exclude && exclude.includes(section.type)));
    if (shown.length === 0) return null;
    if (!integrated) return shown.map(renderSection);
    // 서버가 내려준 기존 섹션 순서를 유지한다.
    return <SectionStack>{shown.map(renderSection)}</SectionStack>;
};

export default HomeList;
